import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, Card, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import md5 from 'md5';
import api from '../../../services/api';
import { requestLaudoPresignDownload } from '../../../services/laudoFiles';
import history from '../../../services/history';
import { getPrefix } from '../../../util/utils';
import allowedToDelete from '../../../helpers/agenda/permissoes';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import useHolter from '../../../hooks/useHolter';

const isRequestAborted = err =>
  err?.name === 'AbortError' ||
  err?.name === 'CanceledError' ||
  err?.code === 'ERR_CANCELED';

export default function ReportHolter() {
  const { id } = useParams();
  const { data: holter, error, isLoading } = useHolter(id);
  const laudoHolter = holter?.[0];
  const { funcionario } = useSelector(state => state.funcionario);
  const medico = useSelector(state => state.doctor.profile);
  const [urls, setURLs] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    const codHolter = laudoHolter?.CODHOLTER;
    if (!codHolter) return undefined;

    const abortController = new AbortController();
    const { signal } = abortController;

    const fetchAndProcessFiles = async () => {
      try {
        const { data } = await api.get('/files', {
          params: { codLaudo: codHolter, procedimento: 'holter' },
          signal,
        });

        if (signal.aborted) return;

        const pdfFiles = data.filter(item => item.Key.includes('.pdf'));
        setFiles(pdfFiles);

        const urlPromises = pdfFiles.map(file =>
          requestLaudoPresignDownload({ key: file.Key }, { signal })
        );

        const urlResults = await Promise.all(urlPromises);
        if (!signal.aborted) setURLs(urlResults);
      } catch (err) {
        if (isRequestAborted(err) || signal.aborted) return;

        toast.error(
          err?.response?.data?.error || 'Erro ao carregar arquivos'
        );
      }
    };

    fetchAndProcessFiles();

    return () => {
      abortController.abort();
      setFiles([]);
      setURLs([]);
    };
  }, [laudoHolter?.CODHOLTER]);

  async function deleteFile(index = 0) {
    try {
      const res = await api.delete('/files', {
        data: { key: files[index].Key },
      });

      toast.success(res.data.message);
      setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
      setURLs(prevUrls => prevUrls.filter((_, i) => i !== index));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao deletar arquivo');
    }
  }

  return (
    <Card className="mt-2">
      <LaudoCardHeader title="LAUDO DE HOLTER" />

      {error && !isLoading && (
        <>
          <span className="text-danger text-center d-block" style={{ marginTop: 10 }}>
            Erro ao carregar laudo de holter! Tente novamente.
          </span>
          <Card.Footer className="text-muted text-center">
            <Button variant="warning" onClick={() => history.goBack()}>
              Voltar
            </Button>
          </Card.Footer>
        </>
      )}

      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <Spinner animation="border" variant="dark" size="lg" />
        </div>
      )}

      {!isLoading && !error && laudoHolter && (
        <>
          <Card.Body>
            <strong className="mr-auto">Nome do Paciente: </strong>
            {`${laudoHolter.CODPAC} - ${laudoHolter.NOMEPAC}`}
            <br></br>
            <strong className="mr-auto">Data de Nasc: </strong>
            {`${laudoHolter.dataNasc}`}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Sexo: </strong>
            {`${laudoHolter.SEXOPAC}`}
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Idade: </strong>
            {`${laudoHolter.idadePAC} anos`}
            <br></br>
            <strong className="mr-auto">Convênio: </strong>
            {laudoHolter.DESCRICONV}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Cod Holter: </strong>
            {laudoHolter.CODHOLTER}&nbsp;&nbsp;&nbsp;
            <strong className="mr-auto">Data do Exame: </strong>
            {laudoHolter.dataHolter}
            {/* <br></br>
            <strong className="mr-auto">
              Médico que Realizou: {getPrefix(laudoHolter.CODREALIZOU)}{' '}
            </strong>
            {`${laudoHolter.Realizou}`} */}
            <br></br>
            <strong className="mr-auto">
              Médico que Solicitou: {getPrefix(laudoHolter.CODSOLICITANTE)}{' '}
            </strong>
            {`${laudoHolter.NOMEMED}`}
            <hr></hr>
            {urls.length > 0 ? (
              urls.map((url, index) => (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    display: 'block',
                    height: '90vh',
                  }}
                >
                  <iframe
                    src={url?.data?.preSignedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="PDF Viewer"
                  />
                </div>
              ))
            ) : (
              <div className="text-center font-italic">
                Não há imagem para exibir
              </div>
            )}
          </Card.Body>
          <Card.Footer className="text-muted text-center">
            <i className="mr-auto">Conferido e assinado eletronicamente por:</i>
            <strong className="mr-auto"> {getPrefix(laudoHolter.CODREALIZOU)} {laudoHolter.Realizou}</strong>
            <br></br>
            <i className="mr-auto">
              Assinatura Digital:{' '}
              {laudoHolter.CODHOLTER && md5(JSON.stringify(laudoHolter.CODHOLTER))}
            </i>

            <div className="mt-2">
              {allowedToDelete(medico, funcionario, 'TODOS') && (
                <Button
                  variant="danger"
                  disabled={files.length === 0}
                  onClick={() => deleteFile(0)}
                >
                  Deletar imagem
                </Button>
              )}{' '}
              <Button variant="warning" onClick={() => history.goBack()}>
                Voltar
              </Button>
            </div>
          </Card.Footer>
        </>
      )}
    </Card>
  );
}
